import cn from "classnames"
import { useMemo, useRef } from "react"
import { useSearchParams } from "react-router-dom"

import { Flex } from "@/atoms"
import { capitalizeFirstLetter } from "@/helpers/string.helpers"
import { FiltersContext } from "@/hooks/useFilters"
import { useTranslation } from "@/hooks/useTranslation"
import { Filter } from "@/organisms/FilterNew/Filter"
import {
  useAdrIncidentsList,
  useEnvironmentsForAdrIncident,
  useRulesForAdrIncident
} from "@/pages/TriagePage/application/useAdrIncidents"
import * as FiltersConsts from "@/pages/TriagePage/const/filters.constants"
import {
  DISMISSED_TAB,
  TAB_KEY
} from "@/pages/TriagePage/const/filters.constants"
import { getUrgentFilterOptions } from "./helpers"

import styles from "./FiltersTabAdrManagement.module.scss"

export const FiltersTabAdrManagement = (props: {
  open: boolean
  config: { enabled: boolean }
}) => {
  const { open, config } = props
  const [searchParams, setSearchParams] = useSearchParams()
  const rulesContext = useRulesForAdrIncident(config)
  const environmentsContext = useEnvironmentsForAdrIncident(config)
  const { noIncidents } = useAdrIncidentsList(config)
  const { t: tGeneral } = useTranslation("translation", {
    keyPrefix: "general"
  })
  const { t: tManagement } = useTranslation("translation", {
    keyPrefix: "triage.adr.management"
  })

  const options = useRef<{
    statusItems: {
      key: string
      title: string
      Icon: React.FC
      options: { label: string; value: string; displayLabel: boolean }[]
      description?: string
    }[]
  }>()

  if (!options.current) {
    options.current = {
      statusItems: [
        {
          key: FiltersConsts.IS_NEW_FILTERS_KEY,
          title: capitalizeFirstLetter(tGeneral("new")),
          Icon: () => null,
          options: [
            { label: tGeneral("yes"), value: "true", displayLabel: false },
            { label: tGeneral("no"), value: "false", displayLabel: false }
          ],
          description: tManagement("filters.newDesc")
        }
      ]
    }
  }

  const { statusItems } = options.current

  const value = useMemo(() => {
    const cb = () => {}

    return {
      searchParams,
      filters: {},
      setFilters: cb,
      onChange: cb,
      searchParamsProps: {
        isDirty: false,
        original: searchParams,
        updateSearchParams: cb
      },
      setSearchParams: cb
    }
  }, [searchParams])

  if (!open || noIncidents) return null

  const isDismissedTab = searchParams.get(TAB_KEY) === DISMISSED_TAB

  const onApply = (params: URLSearchParams) =>
    setSearchParams(new URLSearchParams(params))

  const showFilters = open && !noIncidents

  return (
    <div className={cn(styles.container, styles.containerOpen)}>
      {showFilters && (
        <FiltersContext.Provider value={value}>
          <Flex gap={8}>
            <Filter.Checkbox
              allowApply
              order="none"
              keyFilter={FiltersConsts.ENVIRONMENTS_FILTERS_KEY}
              items={environmentsContext.response?.data || []}
              placeHolderDefaultValue={tManagement("filters.environment")}
              placement="adr-management-page"
              onApply={onApply}
            />
            {!isDismissedTab && (
              <Filter.Radio
                allowApply
                name="status"
                items={statusItems}
                title={tManagement("filters.status")}
                placement="adr-management-page"
                onApply={onApply}
              />
            )}
            {isDismissedTab && (
              <Filter.Checkbox
                allowApply
                order="none"
                keyFilter={FiltersConsts.PRIORITY_FILTERS_KEY}
                items={getUrgentFilterOptions(tManagement)}
                placeHolderDefaultValue={tManagement("filters.priority")}
                placement="adr-management-page"
                onApply={onApply}
              />
            )}
            <Filter.Checkbox
              allowApply
              order="none"
              keyFilter={FiltersConsts.TYPE_FILTERS_KEY}
              items={rulesContext.response?.data?.list || []}
              placeHolderDefaultValue={tManagement("detectionType")}
              placement="adr-management-page"
              onApply={onApply}
            />
          </Flex>
        </FiltersContext.Provider>
      )}
    </div>
  )
}
